import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { type Container } from "@/builder/types";
import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import {
  type AdminApplicationVersion,
  type AdminFlowElement,
  type AdminFlowRequest,
  type AdminIntegrationNode,
} from "../types";

export type ApplicationVersionList = Paginate<
  AdminApplicationVersion<false, true, true, true>
>;

export const applications = createQueryKeys("admin-application-versions", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => ({
    queryKey: [id],
    contextQueries: {
      flowElements: null,
      integrations: null,
    },
  }),
});

const include = "flows.flowElements,flows.children,category";

export const fetchApplicationVersionsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...applications.all(page, search),
    queryFn: () =>
      adminApi
        .get<ApplicationVersionList>(`application/application-versions`, {
          params: {
            include,
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchApplicationVersions = (search?: SearchRequest) =>
  usePaginateQuery((page) =>
    fetchApplicationVersionsQueryOptions(page, search),
  );

export const fetchApplicationVersionQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...applications.one(id),
    queryFn: () =>
      adminApi
        .get<
          AdminApplicationVersion<true, true, true, true>
        >(`application/application-versions/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchApplicationVersion = (id: number | undefined) =>
  useQuery(fetchApplicationVersionQueryOptions(id));

export interface ApplicationRequest {
  name: string | null;
  title: string;
  description?: string | null;
  slug?: string | null;
  image?: string | null;
  action_name?: string | null;
  action_url?: string | null;
  is_wizard: boolean;
  application_category_id: number;
  containers: Container[];
  flows: AdminFlowRequest[];
}

export const useCreateApplicationVersion = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ApplicationRequest) =>
      adminApi
        .post<
          AdminApplicationVersion<true, true, true, true>
        >(`application/application-versions?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
    },
  });
};

export const useUpdateApplicationVersion = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ApplicationRequest) =>
      adminApi
        .put<
          AdminApplicationVersion<true, true, true, true>
        >(`application/application-versions/${id}?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
      queryClient.invalidateQueries(applications.one(id));
    },
  });
};

export const useCreateOrUpdateApplicationVersion = (id: number | undefined) => {
  const createApplication = useCreateApplicationVersion();
  const updateApplication = useUpdateApplicationVersion(id!);
  return id ? updateApplication : createApplication;
};

export const useCloneApplicationVersion = () => {
  return useMutation({
    mutationFn: (id: number) =>
      adminApi
        .post<
          AdminApplicationVersion<false, false, false, false>
        >(`application/application-versions/${id}/clone`)
        .then((response) => response.data),
  });
};

export const useDeleteApplicationVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi.delete(`application/applications/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
      queryClient.invalidateQueries(applications.one(id));
    },
  });
};

export const fetchFlowElementsQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...applications.one(id)._ctx.flowElements,
    queryFn: () =>
      adminApi
        .get<
          AdminFlowElement[]
        >(`application/application-versions/${id}/flows/elements`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchFlowElements = (id: number | undefined) =>
  useQuery(fetchFlowElementsQueryOptions(id));

export const fetchIntegrationNodesQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...applications.one(id)._ctx.integrations,
    queryFn: () =>
      adminApi
        .get<
          AdminIntegrationNode[]
        >(`application/application-versions/${id}/flows/integrations`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchIntegrationNodes = (id: number | undefined) =>
  useQuery(fetchIntegrationNodesQueryOptions(id));

export interface FlowElementRequest {
  id: number;
  roles: AdminFlowElement[];
}

export const useAddElementsToFlows = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: FlowElementRequest) =>
      adminApi
        .put<
          AdminFlowElement[]
        >(`application/application-versions/${id}/flows/elements`, data)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
      queryClient.invalidateQueries(applications.one(id));
    },
  });
};

export const usePublishApplicationVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi
        .put<AdminApplicationVersion>(
          `application/application-versions/${id}/publish`,
        )
        .then((response) => response.data),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
      queryClient.invalidateQueries(applications.one(id));
    },
  });
};

export const useDownloadApplicationVersionsReport = () =>
  useMutation({
    mutationFn: (search?: SearchRequest) =>
      adminApi.post<Blob>(`application/application-versions/report`, search, {
        responseType: "blob",
      }),
  });

export const useDownloadApplicationVersionReport = () =>
  useMutation({
    mutationFn: (id: number) =>
      adminApi.post<Blob>(
        `application/application-versions/${id}/report`,
        undefined,
        {
          responseType: "blob",
        },
      ),
  });
